import type { TtsVoice } from '@/domains/types'
import { classifyVoiceQuality } from './voice-selection'
import type { SpeakCallbacks, SpeakRequest, TtsEngine } from './tts-engine'

/** How long `loadVoices` waits for a `voiceschanged` event before giving up
 * and resolving with whatever `getVoices()` returns (possibly empty) — some
 * browsers (notably Chrome on first load) populate the voice list
 * asynchronously with no guaranteed upper bound, so this can legitimately
 * resolve empty. */
const VOICE_LOAD_TIMEOUT_MS = 1500

function toTtsVoice(voice: SpeechSynthesisVoice): TtsVoice {
  return {
    id: voice.voiceURI,
    name: voice.name,
    lang: voice.lang,
    quality: classifyVoiceQuality(voice.name),
    isDefault: voice.default,
  }
}

/**
 * `TtsEngine` backed by the browser's Web Speech API
 * (`window.speechSynthesis`). A generation token guards every callback
 * against a since-superseded `speak()`/`cancel()` call — `cancel()`'s own
 * `synth.cancel()` fires `onerror('interrupted')` on the utterance it just
 * cancelled, which must never reach a caller that has already moved on.
 */
export class WebSpeechEngine implements TtsEngine {
  // eslint-disable-next-line id-length -- required literal property name from the `TtsEngine` interface
  readonly id = 'web-speech' as const

  private generation = 0
  /** Kept for Safari, which (per WebKit bug history) can garbage-collect an
   * utterance mid-speech if nothing else references it. */
  private currentUtterance: SpeechSynthesisUtterance | null = null
  private voicesCache: SpeechSynthesisVoice[] = []

  isSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'speechSynthesis' in window &&
      typeof window.SpeechSynthesisUtterance === 'function'
    )
  }

  private synth(): SpeechSynthesis {
    return window.speechSynthesis
  }

  async loadVoices(signal?: AbortSignal): Promise<TtsVoice[]> {
    if (!this.isSupported()) return []

    const synth = this.synth()
    const immediate = synth.getVoices()
    if (immediate.length > 0) {
      this.voicesCache = immediate
      return immediate.map(toTtsVoice)
    }

    const voices = await new Promise<SpeechSynthesisVoice[]>((resolve) => {
      let settled = false
      const finish = (result: SpeechSynthesisVoice[]) => {
        if (settled) return
        settled = true
        synth.removeEventListener('voiceschanged', onVoicesChanged)
        clearTimeout(timeoutId)
        resolve(result)
      }
      const onVoicesChanged = () => finish(synth.getVoices())
      synth.addEventListener('voiceschanged', onVoicesChanged)
      const timeoutId = setTimeout(() => finish(synth.getVoices()), VOICE_LOAD_TIMEOUT_MS)
      signal?.addEventListener('abort', () => finish([]))
    })

    this.voicesCache = voices
    return voices.map(toTtsVoice)
  }

  onVoicesChanged(listener: (voices: TtsVoice[]) => void): () => void {
    if (!this.isSupported()) return () => {}

    const synth = this.synth()
    const handleVoicesChanged = () => {
      this.voicesCache = synth.getVoices()
      listener(this.voicesCache.map(toTtsVoice))
    }
    synth.addEventListener('voiceschanged', handleVoicesChanged)
    return () => synth.removeEventListener('voiceschanged', handleVoicesChanged)
  }

  speak(request: SpeakRequest, callbacks: SpeakCallbacks): void {
    this.cancel()
    const generation = this.generation
    const synth = this.synth()

    const utterance = new SpeechSynthesisUtterance(request.text)
    utterance.lang = request.lang
    utterance.rate = request.rate

    const voice = request.voiceId
      ? this.voicesCache.find((candidate) => candidate.voiceURI === request.voiceId)
      : undefined
    if (voice) utterance.voice = voice

    utterance.onstart = () => {
      if (generation !== this.generation) return
      callbacks.onStart()
    }
    utterance.onend = () => {
      if (generation !== this.generation) return
      callbacks.onEnd()
    }
    utterance.onerror = (event) => {
      if (generation !== this.generation) return
      if (event.error === 'interrupted' || event.error === 'canceled') {
        callbacks.onError('interrupted')
        return
      }
      callbacks.onError('failed')
    }

    this.currentUtterance = utterance
    synth.speak(utterance)
  }

  cancel(): void {
    this.generation += 1
    // Detach the outgoing utterance's handlers before cancelling: Safari has
    // been known to fire a stray event on an utterance object that's still
    // referenced from elsewhere after `SpeechSynthesis.cancel()`, and the
    // generation token alone doesn't stop it from mutating state no one
    // reads anymore.
    if (this.currentUtterance) {
      this.currentUtterance.onstart = null
      this.currentUtterance.onend = null
      this.currentUtterance.onerror = null
    }
    if (this.isSupported()) this.synth().cancel()
    this.currentUtterance = null
  }

  isSpeaking(): boolean {
    return this.isSupported() && this.synth().speaking
  }
}

let singleton: WebSpeechEngine | null = null

/** Process-wide `WebSpeechEngine` instance — `speechSynthesis` is itself a
 * singleton browser resource, so every `useReadAloud` consumer shares one
 * engine rather than fighting over cancel/speak calls. */
export function getWebSpeechEngine(): WebSpeechEngine {
  if (!singleton) singleton = new WebSpeechEngine()
  return singleton
}
